/*
 * Quantity rules: per-product minimum quantity and step.
 * Rules are read from data-qty-* attributes rendered by snippets/quantity-rule-attrs.liquid.
 * Spec: docs/superpowers/specs/2026-09-29-quantity-rules-design.md
 */
(function (root) {
  "use strict";

  function toInt(value) {
    const number = parseInt(value, 10);
    return Number.isFinite(number) ? number : NaN;
  }

  const QuantityRules = {
    read(input) {
      if (!input || !input.dataset || input.dataset.qtyStep === undefined) return null;
      const step = Math.max(1, toInt(input.dataset.qtyStep) || 1);
      const min = Math.max(1, toInt(input.dataset.qtyMin) || step);
      const max = toInt(input.dataset.qtyMax);
      return {
        min: Math.ceil(min / step) * step,
        step,
        max: Number.isFinite(max) ? Math.max(0, max) : null,
        zero: input.dataset.qtyZero !== undefined,
      };
    },

    buyable(rule) {
      return rule.max === null || rule.max >= rule.min;
    },

    isValid(value, rule) {
      if (value === 0) return rule.zero;
      return (
        Number.isInteger(value) &&
        value >= rule.min &&
        value % rule.step === 0 &&
        (rule.max === null || value <= rule.max)
      );
    },

    snap(value, rule) {
      const quantity = Math.floor(value);
      if (!Number.isFinite(quantity) || quantity <= 0 || !QuantityRules.buyable(rule)) {
        return rule.zero ? 0 : rule.min;
      }
      const snapped = Math.ceil(Math.max(quantity, rule.min) / rule.step) * rule.step;
      if (rule.max !== null && snapped > rule.max) return Math.floor(rule.max / rule.step) * rule.step;
      return snapped;
    },

    next(value, rule, direction) {
      const quantity = Number.isFinite(value) ? Math.floor(value) : 0;
      if (direction > 0) {
        const up = Math.max(rule.min, (Math.floor(quantity / rule.step) + 1) * rule.step);
        if (!QuantityRules.buyable(rule) || (rule.max !== null && up > rule.max)) {
          return QuantityRules.snap(quantity, rule);
        }
        return up;
      }
      if (rule.zero && quantity <= rule.min) return 0;
      const down = (Math.ceil(quantity / rule.step) - 1) * rule.step;
      if (down < rule.min) return rule.zero ? 0 : rule.min;
      return down;
    },
  };

  root.QuantityRules = QuantityRules;
  if (typeof module === "object" && module.exports) module.exports = QuantityRules;
  if (typeof document === "undefined") return;

  const RULE_INPUT = "input[data-qty-step]";

  function ruleInputs(form) {
    return Array.from((form && form.elements) || []).filter(
      (element) => element.matches && element.matches(RULE_INPUT)
    );
  }

  function setValue(input, value) {
    const text = String(value);
    if (input.value === text) return false;
    input.value = text;
    return true;
  }

  function snapInput(input) {
    const rule = QuantityRules.read(input);
    return rule ? setValue(input, QuantityRules.snap(parseInt(input.value, 10), rule)) : false;
  }

  function showMessages(form) {
    if (!form.id) return;
    document.querySelectorAll(`[data-qty-msg-for="${form.id}"]`).forEach((message) => {
      message.hidden = false;
    });
  }

  function guard(event, form) {
    if (!form || !form.elements) return;
    let blocked = false;
    ruleInputs(form).forEach((input) => {
      const rule = QuantityRules.read(input);
      if (QuantityRules.buyable(rule)) snapInput(input);
      else if (!(rule.zero && parseInt(input.value, 10) === 0)) blocked = true;
    });
    if (!blocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showMessages(form);
  }

  // +/− of <quantity-input> and <quantity-input-bundle>: take over from the theme so every
  // step lands on a valid quantity, then fire "change" so the theme's own handlers still run.
  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest && event.target.closest("button[name='plus'], button[name='minus']");
      const box = button && button.closest("quantity-input, quantity-input-bundle");
      const input = box && box.querySelector(RULE_INPUT);
      if (!input) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const direction = button.name === "plus" ? 1 : -1;
      const nextValue = QuantityRules.next(parseInt(input.value, 10), QuantityRules.read(input), direction);
      if (setValue(input, nextValue)) input.dispatchEvent(new Event("change", { bubbles: true }));
    },
    true
  );

  // Typed values: snap before the theme's handlers (and the cart's debounced update) read them.
  document.addEventListener(
    "change",
    (event) => {
      if (event.target.matches && event.target.matches(RULE_INPUT)) snapInput(event.target);
    },
    true
  );

  // Last check before anything is sent: product forms, cart checkout, Enter-key submits.
  document.addEventListener("submit", (event) => guard(event, event.target), true);

  // Send buttons that live outside their form (grouped products) name it in data-qty-form.
  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest && event.target.closest("[data-qty-form]");
      if (button) guard(event, document.getElementById(button.dataset.qtyForm));
    },
    true
  );

  // "Adaugă încă N": one more step, through the stepper's own plus button.
  document.addEventListener("click", (event) => {
    const button = event.target.closest && event.target.closest("[data-qty-add-more]");
    if (!button) return;
    event.preventDefault();
    const scope = button.closest("[data-qty-scope]");
    const plus = scope && scope.querySelector("button[name='plus']");
    if (plus) plus.click();
  });

  // Variant change on the product page and quick view: apply that variant's stock cap.
  document.addEventListener("change", (event) => {
    const idInput = event.target;
    if (!idInput.matches || !idInput.matches("input[name='id']") || !idInput.form) return;
    const capsElement = idInput.form.querySelector("script[data-qty-caps]");
    if (!capsElement) return;
    let caps;
    try {
      caps = JSON.parse(capsElement.textContent);
    } catch (error) {
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(caps, idInput.value)) return;
    const cap = caps[idInput.value];
    let buyable = true;
    ruleInputs(idInput.form).forEach((input) => {
      if (cap === null) {
        delete input.dataset.qtyMax;
        input.removeAttribute("max");
      } else {
        input.dataset.qtyMax = String(cap);
        if (input.dataset.qtyZero === undefined) input.max = String(cap);
      }
      buyable = buyable && QuantityRules.buyable(QuantityRules.read(input));
      snapInput(input);
      const box = input.closest("quantity-input");
      if (box && typeof box.validateQtyRules === "function") box.validateQtyRules();
    });
    idInput.form.querySelectorAll("[data-qty-stock-note]").forEach((note) => {
      note.hidden = buyable;
    });
    idInput.form.querySelectorAll(".btn-checkout-dynamic").forEach((wrapper) => {
      wrapper.hidden = !buyable;
    });
    // The theme resets the add button right after this event; when the new variant has stock
    // but less than the minimum, disable it again once the theme is done. A button the theme
    // itself disabled (sold out) keeps its own label.
    const addButton = idInput.form.querySelector("[name='add']");
    if (!addButton || buyable) return;
    setTimeout(() => {
      if (addButton.disabled) return;
      addButton.disabled = true;
      const label = addButton.querySelector(".btn-label");
      const text = root.quantityRuleStrings && root.quantityRuleStrings.insufficient;
      if (label && text) label.textContent = text;
    }, 0);
  });

  // Cart safety net: Shopify flags invalid cart lines (data-qty-invalid) when it renders the
  // cart page and the drawer. Fix them here; lines that can't be fixed are never removed.
  const RELOAD_KEY = "qtyRulesReloadAt";
  const NOTES_KEY = "qtyRulesNotes";
  const RELOAD_WINDOW = 10000;
  const drawerAttempts = new Set();
  const drawerNotes = {};

  QuantityRules.reload = () => root.location.reload();

  function session() {
    try {
      return root.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function reloadedRecently() {
    const store = session();
    return Date.now() - Number((store && store.getItem(RELOAD_KEY)) || 0) < RELOAD_WINDOW;
  }

  function reloadOnce() {
    if (reloadedRecently()) return;
    const store = session();
    if (store) store.setItem(RELOAD_KEY, String(Date.now()));
    QuantityRules.reload();
  }

  function invalidLines(form) {
    return ruleInputs(form).filter((input) => input.dataset.qtyInvalid !== undefined);
  }

  function writeNotes(form, notes) {
    if (!form) return;
    const template = (root.quantityRuleStrings && root.quantityRuleStrings.adjusted) || "";
    Object.keys(notes).forEach((key) => {
      const input = form.querySelector(`input[data-id="${key}"]`);
      const line = input && input.closest("[data-qty-line]");
      const note = line && line.querySelector("[data-qty-note]");
      if (!note) return;
      const text = template.replace("[quantity]", notes[key]);
      if (note.textContent !== text) note.textContent = text;
      note.hidden = false;
    });
  }

  function fixCartPage() {
    const form = document.getElementById("cart");
    if (!form || !document.body.classList.contains("template-cart") || reloadedRecently()) return;
    const updates = {};
    invalidLines(form).forEach((input) => {
      const rule = QuantityRules.read(input);
      if (input.dataset.id && QuantityRules.buyable(rule)) {
        updates[input.dataset.id] = QuantityRules.snap(parseInt(input.value, 10), rule);
      }
    });
    if (!Object.keys(updates).length) return;
    root
      .fetch(`${root.routes.cart_update_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      })
      .then((response) => {
        if (!response.ok) return;
        const store = session();
        if (store) store.setItem(NOTES_KEY, JSON.stringify(updates));
        reloadOnce();
      })
      .catch(() => {});
  }

  function onCartPageRender() {
    if (invalidLines(document.getElementById("cart")).length) {
      fixCartPage();
    } else if (document.querySelector("[data-qty-msg-for='cart'][data-qty-cart-blocked]")) {
      reloadOnce();
    }
  }

  function fixDrawer() {
    const form = document.getElementById("minicart");
    if (!form) return;
    writeNotes(form, drawerNotes);
    const input = invalidLines(form).find(
      (line) => !drawerAttempts.has(line.dataset.id) && QuantityRules.buyable(QuantityRules.read(line))
    );
    if (!input) return;
    const target = QuantityRules.snap(parseInt(input.value, 10), QuantityRules.read(input));
    drawerAttempts.add(input.dataset.id);
    drawerNotes[input.dataset.id] = target;
    input.value = String(target);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function whenChanged(element, callback) {
    if (!element) return;
    let timer;
    new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(callback, 50);
    }).observe(element, { childList: true, subtree: true });
  }

  function startCartSafetyNet() {
    const store = session();
    let notes = {};
    try {
      notes = JSON.parse((store && store.getItem(NOTES_KEY)) || "{}");
    } catch (error) {
      notes = {};
    }
    if (store) store.removeItem(NOTES_KEY);
    writeNotes(document.getElementById("cart"), notes);
    fixCartPage();
    fixDrawer();
    whenChanged(document.getElementById("minicart-form"), fixDrawer);
    whenChanged(document.querySelector("cart-items"), onCartPageRender);
  }

  // Deferred scripts run before DOMContentLoaded; wait for it so theme.js has defined the
  // cart elements whose change handlers do the drawer's updates.
  if (document.readyState === "complete") startCartSafetyNet();
  else document.addEventListener("DOMContentLoaded", startCartSafetyNet);
})(typeof window !== "undefined" ? window : globalThis);
